import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { Logo } from '../components/Logo';
import { blogPosts, BlogPost } from '../data/blogPosts';
const escapeHtml = (str: string) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const renderInline = (text: string) => {
  // Links
  let out = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  // Bold
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Italic
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  // Inline code
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  return out;
};
const renderMarkdown = (md: string) => {
  const lines = md.split('\n');
  const html: string[] = [];
  let i = 0;
  const flushList = (buffer: string[], type: 'ul' | 'ol') => {
    if (!buffer.length) return;
    html.push(`<${type}>${buffer.map(item => `<li>${renderInline(item)}</li>`).join('')}</${type}>`);
    buffer.length = 0;
  };
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();
    // Skip blank
    if (!line) {
      i++;
      continue;
    }
    // Headings
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      html.push(`<h${level}>${renderInline(h[2])}</h${level}>`);
      i++;
      continue;
    }
    // Blockquote
    if (line.startsWith('>')) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        buf.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      html.push(`<blockquote>${renderInline(buf.join(' '))}</blockquote>`);
      continue;
    }
    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        buf.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i++;
      }
      flushList(buf, 'ul');
      continue;
    }
    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        buf.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i++;
      }
      flushList(buf, 'ol');
      continue;
    }
    // Paragraph (gather consecutive non-empty, non-special lines)
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i].trim();
      if (!l || /^(#{1,6})\s/.test(l) || l.startsWith('>') || /^[-*]\s/.test(l) || /^\d+\.\s/.test(l)) {
        break;
      }
      para.push(l);
      i++;
    }
    if (para.length) {
      html.push(`<p>${renderInline(para.join(' '))}</p>`);
    }
  }
  return html.join('\n');
};
const estimateReadTime = (content: string) => {
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 220));
  return `${minutes} min read`;
};
export const BlogPostPage = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    const currentPost = blogPosts.find(p => p.id === id);
    if (!currentPost) {
      navigate('/blog');
      return;
    }
    setPost(currentPost);
    const related = blogPosts.filter(p => p.category === currentPost.category && p.id !== id).slice(0, 3);
    setRelatedPosts(related);
  }, [id, navigate]);
  const renderedContent = useMemo(() => post ? renderMarkdown(post.content) : '', [post]);
  if (!post) {
    return <div className="min-h-screen bg-[#f2efeb] flex items-center justify-center text-[#c9a875]" data-id="element-740">
        Loading...
      </div>;
  }
  return <div className="bg-[#f2efeb] text-[#2b2826] min-h-screen w-full" data-id="element-741">
      {/* Navigation */}
      <nav className="absolute top-0 left-0 w-full z-50 py-6 px-4 sm:px-6 lg:px-8" data-id="element-742">
        <div className="max-w-7xl mx-auto flex justify-between items-center bg-[#2b2826] backdrop-blur-md py-3 px-6 rounded-full border border-[#2b2826] shadow-lg" data-id="element-743">
          <Link to="/" data-id="element-744">
            <Logo data-id="element-745" />
          </Link>

          <div className="hidden md:flex items-center gap-8" data-id="element-746">
            <Link to="/" className="text-[#f2efeb] font-medium hover:text-[#c9a875] transition-colors" data-id="element-747">
              Home
            </Link>
            <Link to="/#services" className="text-[#f2efeb] font-medium hover:text-[#c9a875] transition-colors" data-id="element-748">
              Services
            </Link>
            <Link to="/#process" className="text-[#f2efeb] font-medium hover:text-[#c9a875] transition-colors" data-id="element-749">
              How It Works
            </Link>
            <Link to="/blog" className="text-[#c9a875] font-medium transition-colors" data-id="element-750">
              Blog
            </Link>
            <Link to="/#funding-form" className="bg-[#c9a875] px-5 py-2.5 rounded-full text-white hover:bg-[#b08e5c] transition-all focus:outline-none focus:ring-2 focus:ring-[#c9a875]/50 text-sm" data-id="element-751">
              Get Funded
            </Link>
          </div>

          <div className="md:hidden" data-id="element-752">
            <Link to="/#funding-form" className="bg-[#c9a875] px-4 py-2 rounded-full text-white hover:bg-[#b08e5c] transition-all text-sm font-medium" data-id="element-753">
              Get Funded
            </Link>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article data-id="element-754">
        <header className="pt-36 sm:pt-44 pb-10 sm:pb-14 px-4 sm:px-6 lg:px-8" data-id="element-755">
          <div className="max-w-3xl mx-auto" data-id="element-756">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#2b2826]/60 hover:text-[#c9a875] transition-colors mb-8" data-id="element-757">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-758" />
              Back to all articles
            </Link>

            <p className="text-xs tracking-widest uppercase text-[#c9a875] font-medium mb-5" data-id="element-759">
              {post.category}
            </p>

            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#2b2826] leading-tight mb-8" data-id="element-760">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#2b2826]/60" data-id="element-761">
              <div className="flex items-center gap-2.5" data-id="element-762">
                <div className="w-9 h-9 rounded-full bg-[#2b2826] flex items-center justify-center text-[#c9a875] font-serif-display text-base" data-id="element-763">
                  {post.author.charAt(0)}
                </div>
                <span className="text-[#2b2826] font-medium" data-id="element-764">
                  {post.author}
                </span>
              </div>
              <span className="hidden sm:inline w-1 h-1 rounded-full bg-[#2b2826]/30" data-id="element-765" />
              <span className="flex items-center gap-1.5" data-id="element-766">
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-767" />
                {post.date}
              </span>
              <span className="hidden sm:inline w-1 h-1 rounded-full bg-[#2b2826]/30" data-id="element-768" />
              <span className="flex items-center gap-1.5" data-id="element-769">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-770" />
                {estimateReadTime(post.content)}
              </span>
            </div>
          </div>
        </header>

        {/* Hero Image */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-16" data-id="element-771">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden" data-id="element-772">
            <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" data-id="element-773" />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24" data-id="element-774">
          <div className="article-prose" dangerouslySetInnerHTML={{
          __html: renderedContent
        }} data-id="element-775" />
        </div>
      </article>

      {/* Inline article-prose styles */}
      <style data-id="element-776">{`
        .article-prose {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 1.0625rem;
          line-height: 1.75;
          color: rgba(43, 40, 38, 0.85);
        }
        .article-prose h1 {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 2.5rem;
          line-height: 1.15;
          letter-spacing: -0.02em;
          color: #2b2826;
          margin: 3rem 0 1.5rem;
          font-weight: 400;
        }
        .article-prose h2 {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 2rem;
          line-height: 1.2;
          letter-spacing: -0.02em;
          color: #2b2826;
          margin: 2.75rem 0 1.25rem;
          font-weight: 400;
        }
        .article-prose h3 {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.5rem;
          line-height: 1.3;
          letter-spacing: -0.01em;
          color: #2b2826;
          margin: 2.25rem 0 1rem;
          font-weight: 400;
        }
        .article-prose p {
          margin: 0 0 1.5rem;
        }
        .article-prose strong {
          color: #2b2826;
          font-weight: 600;
        }
        .article-prose em {
          font-style: italic;
        }
        .article-prose a {
          color: #c9a875;
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-underline-offset: 3px;
        }
        .article-prose a:hover {
          color: #b08e5c;
        }
        .article-prose ul,
        .article-prose ol {
          margin: 0 0 1.75rem;
          padding-left: 1.25rem;
        }
        .article-prose ul {
          list-style: none;
          padding-left: 0;
        }
        .article-prose ul li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .article-prose ul li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.7rem;
          width: 6px;
          height: 6px;
          border-radius: 9999px;
          background: #c9a875;
        }
        .article-prose ol li {
          margin-bottom: 0.5rem;
          padding-left: 0.25rem;
        }
        .article-prose blockquote {
          border-left: 3px solid #c9a875;
          padding: 0.25rem 0 0.25rem 1.5rem;
          margin: 2rem 0;
          font-family: 'Instrument Serif', Georgia, serif;
          font-style: italic;
          font-size: 1.5rem;
          line-height: 1.4;
          color: #2b2826;
        }
        .article-prose code {
          background: rgba(43, 40, 38, 0.06);
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          font-size: 0.9em;
          font-family: ui-monospace, SFMono-Regular, monospace;
          color: #2b2826;
        }
        .article-prose > *:first-child {
          margin-top: 0;
        }
      `}</style>

      {/* CTA */}
      <div className="bg-[#2b2826] py-20 border-t border-[#403a35]" data-id="element-777">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-778">
          <p className="text-xs tracking-widest uppercase text-[#c9a875] font-medium mb-4" data-id="element-779">
            Ready to apply?
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#f2efeb] mb-5 leading-tight" data-id="element-780">
            Fund your next deal in{' '}
            <span className="italic text-[#c9a875]" data-id="element-781">24–48 hours</span>
          </h2>
          <p className="text-[#f2efeb]/70 mb-8 max-w-xl mx-auto font-light" data-id="element-782">
            No credit checks, no upfront fees, no surprises. Submit your deal
            and our team will be in touch.
          </p>
          <Link to="/#funding-form" className="inline-flex items-center gap-2 bg-[#c9a875] hover:bg-[#b08e5c] text-white font-medium py-3.5 px-7 rounded-full transition-colors text-sm" data-id="element-783">
            Apply for funding
            <ArrowRightIcon className="w-4 h-4" data-id="element-784" />
          </Link>
        </div>
      </div>

      {/* Related */}
      {relatedPosts.length > 0 && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24" data-id="element-785">
          <div className="flex items-end justify-between mb-10" data-id="element-786">
            <div data-id="element-787">
              <p className="text-xs tracking-widest uppercase text-[#c9a875] font-medium mb-3" data-id="element-788">
                Keep reading
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2b2826] leading-tight" data-id="element-789">
                Related articles
              </h2>
            </div>
            <Link to="/blog" className="hidden sm:inline-flex items-center gap-2 text-[#2b2826] font-medium text-sm hover:text-[#c9a875] transition-colors group" data-id="element-790">
              View all
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-791" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12" data-id="element-792">
            {relatedPosts.map(relatedPost => <Link key={relatedPost.id} to={`/blog/${relatedPost.id}`} className="group block" data-id="element-793">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-5" data-id="element-794">
                  <img src={relatedPost.imageUrl} alt={relatedPost.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" data-id="element-795" />
                  <div className="absolute top-3 left-3" data-id="element-796">
                    <span className="bg-[#f2efeb]/95 backdrop-blur-sm text-[#2b2826] text-[10px] font-medium tracking-wider uppercase px-2.5 py-1 rounded-full" data-id="element-797">
                      {relatedPost.category}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-[#2b2826]/55 mb-3" data-id="element-798">
                  <span data-id="element-799">{relatedPost.date}</span>
                  <span className="w-0.5 h-0.5 rounded-full bg-[#2b2826]/30" data-id="element-800" />
                  <span data-id="element-801">{estimateReadTime(relatedPost.content)}</span>
                </div>

                <h3 className="font-serif-display text-xl text-[#2b2826] leading-snug group-hover:text-[#c9a875] transition-colors line-clamp-2" data-id="element-802">
                  {relatedPost.title}
                </h3>
              </Link>)}
          </div>
        </div>}

      <Footer data-id="element-803" />
    </div>;
};